/**
 * The contract between the Python engine, the gateway and the web app.
 *
 * These schemas are the single place the three services agree on field names,
 * and they are *parsed*, not cast: a shape change in the engine surfaces as a
 * validation error at the boundary rather than as `undefined` rendering into
 * the UI as a blank number.
 */
import { z } from "zod";
export const TIMEFRAMES = [
    "1s", "5s", "15s", "30s",
    "1m", "2m", "3m", "5m", "10m", "15m", "30m", "45m",
    "1h", "2h", "3h", "4h",
    "1d", "1w", "1M",
];
/** Grouping for the timeframe menu, matching the engine's own registry. */
export const TIMEFRAME_GROUPS = [
    { group: "Seconds", items: ["1s", "5s", "15s", "30s"] },
    { group: "Minutes", items: ["1m", "2m", "3m", "5m", "10m", "15m", "30m", "45m"] },
    { group: "Hours", items: ["1h", "2h", "3h", "4h"] },
    { group: "Days", items: ["1d", "1w", "1M"] },
];
/** Compact labels for the toolbar. */
export const TIMEFRAME_LABEL = {
    "1s": "1 second", "5s": "5 seconds", "15s": "15 seconds", "30s": "30 seconds",
    "1m": "1 minute", "2m": "2 minutes", "3m": "3 minutes", "5m": "5 minutes",
    "10m": "10 minutes", "15m": "15 minutes", "30m": "30 minutes", "45m": "45 minutes",
    "1h": "1 hour", "2h": "2 hours", "3h": "3 hours", "4h": "4 hours",
    "1d": "1 day", "1w": "1 week", "1M": "1 month",
};
export const Timeframe = z.enum(TIMEFRAMES);
// ─────────────────────────── market data ───────────────────────────
export const Bar = z.object({
    time: z.number().int(),
    open: z.number(),
    high: z.number(),
    low: z.number(),
    close: z.number(),
    volume: z.number(),
});
export const BarsResponse = z.object({
    symbol: z.string(),
    timeframe: Timeframe,
    bars: z.array(Bar),
    provider: z.string(),
    delayed: z.boolean(),
    disclaimer: z.string().nullable().optional(),
    /** regular | extended — which trading sessions these bars cover. */
    session: z.string().default("regular"),
    /** Why intraday volume does not sum to the daily bar's volume. */
    volume_note: z.string().nullable().optional(),
});
export const Quote = z.object({
    symbol: z.string(),
    price: z.number(),
    ts: z.string(),
    change: z.number().nullable().optional(),
    change_pct: z.number().nullable().optional(),
    prev_close: z.number().nullable().optional(),
    day_open: z.number().nullable().optional(),
    day_high: z.number().nullable().optional(),
    day_low: z.number().nullable().optional(),
    volume: z.number().nullable().optional(),
    /** The inside market, when the provider serves quotes and not just trades. */
    bid: z.number().nullable().optional(),
    ask: z.number().nullable().optional(),
    delayed: z.boolean().default(false),
});
export const SymbolHit = z.object({
    symbol: z.string(),
    name: z.string().nullable().optional(),
    exchange: z.string().nullable().optional(),
    sector: z.string().nullable().optional(),
});
// ─────────────────────────── pine ───────────────────────────
export const PineErrorDetail = z.object({
    message: z.string(),
    line: z.number().default(0),
    col: z.number().default(0),
    kind: z.string().default("error"),
    hint: z.string().nullable().optional(),
    bar: z.number().optional(),
});
export const Plot = z.object({
    id: z.string(),
    title: z.string(),
    kind: z.string(),
    values: z.array(z.number().nullable()),
    colors: z.array(z.string().nullable()).nullable().optional(),
    color: z.string().nullable().optional(),
    linewidth: z.number().default(1),
    style: z.string().nullable().optional(),
    overlay: z.boolean().default(true),
});
export const PineInput = z.object({
    name: z.string(),
    kind: z.string(),
    default: z.unknown(),
    title: z.string(),
    options: z.array(z.unknown()).nullable().optional(),
    minval: z.unknown().nullable().optional(),
    maxval: z.unknown().nullable().optional(),
    step: z.unknown().nullable().optional(),
    group: z.string().nullable().optional(),
    tooltip: z.string().nullable().optional(),
});
export const PineLabel = z.object({
    bar: z.number(),
    y: z.number().nullable(),
    text: z.string(),
    color: z.string().nullable(),
    textcolor: z.string().nullable(),
    /** label_up | label_down | label_left | ... */
    style: z.string(),
    /** price | abovebar | belowbar -- abovebar and belowbar ignore `y`. */
    yloc: z.string(),
});
export const PineTableCell = z.object({
    column: z.number(),
    row: z.number(),
    text: z.string(),
    text_color: z.string().nullable(),
    bgcolor: z.string().nullable(),
    halign: z.string().nullable(),
    valign: z.string().nullable(),
    size: z.string().nullable(),
    tooltip: z.string().nullable(),
});
/** A stats panel. Cells are keyed "column,row" because a sparse grid is what
 *  a script actually fills, and JSON has no tuple keys. */
export const PineTable = z.object({
    id: z.string(),
    position: z.string(),
    columns: z.number(),
    rows: z.number(),
    bgcolor: z.string().nullable(),
    border_width: z.number(),
    border_color: z.string().nullable(),
    frame_width: z.number(),
    frame_color: z.string().nullable(),
    cells: z.record(PineTableCell),
});
export const PineLine = z.object({
    id: z.string(),
    x1: z.number().nullable(), y1: z.number().nullable(),
    x2: z.number().nullable(), y2: z.number().nullable(),
    xloc: z.string(),
    /** none | left | right | both */
    extend: z.string(),
    color: z.string().nullable(),
    style: z.string(),
    width: z.number(),
});
export const PineBox = z.object({
    id: z.string(),
    left: z.number().nullable(), top: z.number().nullable(),
    right: z.number().nullable(), bottom: z.number().nullable(),
    border_color: z.string().nullable(),
    border_width: z.number(),
    border_style: z.string(),
    extend: z.string(),
    xloc: z.string(),
    bgcolor: z.string().nullable(),
    text: z.string(),
    text_color: z.string().nullable(),
    text_halign: z.string().nullable(),
    text_valign: z.string().nullable(),
});
export const PinePolyline = z.object({
    id: z.string(),
    points: z.array(z.object({
        index: z.number().nullable(),
        time: z.number().nullable(),
        price: z.number().nullable(),
    })),
    curved: z.boolean(),
    closed: z.boolean(),
    xloc: z.string(),
    line_color: z.string().nullable(),
    fill_color: z.string().nullable(),
    line_style: z.string(),
    line_width: z.number(),
});
export const PineLinefill = z.object({
    id: z.string(),
    line1: z.string().nullable(),
    line2: z.string().nullable(),
    color: z.string().nullable(),
});
export const PineRunResponse = z.object({
    ok: z.boolean(),
    symbol: z.string(),
    timeframe: Timeframe,
    declaration: z.record(z.unknown()),
    times: z.array(z.number()),
    plots: z.array(Plot),
    hlines: z.array(z.object({
        price: z.number().nullable(),
        title: z.string(),
        color: z.string(),
        style: z.string(),
        linewidth: z.number(),
    })),
    fills: z.array(z.object({
        plot_a: z.string(), plot_b: z.string(), color: z.string(), title: z.string(),
    })),
    bgcolors: z.array(z.string().nullable()),
    inputs: z.array(PineInput),
    alerts: z.array(z.object({
        id: z.string(), title: z.string(), message: z.string(),
        bars: z.array(z.number()), count: z.number(),
    })),
    /** alert() firings: one per firing, each with its own rendered message. */
    alert_events: z.array(z.object({
        bar: z.number(), message: z.string(), freq: z.string(),
    })).default([]),
    labels: z.array(PineLabel),
    tables: z.array(PineTable).default([]),
    lines: z.array(PineLine).default([]),
    boxes: z.array(PineBox).default([]),
    polylines: z.array(PinePolyline).default([]),
    linefills: z.array(PineLinefill).default([]),
    orders: z.array(z.record(z.unknown())),
    warnings: z.array(z.string()),
    errors: z.array(PineErrorDetail),
    bars: z.number(),
    elapsed_ms: z.number(),
});
export const ValidateResponse = z.object({
    ok: z.boolean(),
    version: z.number().optional(),
    statements: z.number().optional(),
    call_sites: z.number().optional(),
    errors: z.array(PineErrorDetail),
});
export const Script = z.object({
    id: z.number(),
    name: z.string(),
    language: z.string(),
    kind: z.string(),
    source: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
});
// ─────────────────────────── calibration ───────────────────────────
/**
 * The verdict vocabulary. `measured-edge` is the only value that licenses the
 * UI to present a signal as having worked, and it requires n >= 30 independent
 * observations, a positive excess over baseline, q < 0.10 and fold consistency
 * >= 0.6 -- all decided server-side.
 */
export const Verdict = z.enum(["measured-edge", "not-significant", "no-edge", "untested"]);
export const Confidence = z.enum(["insufficient", "very-low", "low", "moderate", "good"]);
export const VerdictDetail = z.object({
    verdict: Verdict,
    hit_rate: z.number().nullable(),
    interval: z.tuple([z.number(), z.number()]),
    baseline: z.number().nullable(),
    excess: z.number().nullable(),
    samples: z.number(),
    confidence: Confidence,
    notes: z.array(z.string()).default([]),
});
export const CalibrationResult = z.object({
    signal_id: z.string(),
    label: z.string().nullable().optional(),
    family: z.string().nullable().optional(),
    scope: z.string(),
    tf: z.string(),
    horizon_bars: z.number(),
    split: z.string(),
    n: z.number(),
    n_independent: z.number(),
    wins: z.number(),
    hit_rate: z.number(),
    ci_low: z.number(),
    ci_high: z.number(),
    avg_return: z.number().nullable(),
    median_return: z.number().nullable(),
    avg_mfe: z.number().nullable(),
    avg_mae: z.number().nullable(),
    profit_factor: z.number().nullable(),
    baseline_hit_rate: z.number().nullable(),
    baseline_avg_return: z.number().nullable(),
    excess_vs_baseline: z.number(),
    p_value: z.number(),
    q_value: z.number().nullable(),
    fold_consistency: z.number().nullable(),
    folds_positive: z.number(),
    folds_total: z.number(),
    confidence: Confidence,
    first_event: z.string().nullable(),
    last_event: z.string().nullable(),
    notes: z.array(z.string()).default([]),
    verdict: VerdictDetail,
});
export const CalibrationRun = z.object({
    universe: z.array(z.string()),
    skipped: z.record(z.string()),
    timeframe: z.string(),
    horizons: z.array(z.number()),
    window: z.object({
        start: z.string().nullable(),
        end: z.string().nullable(),
        bars: z.number(),
    }),
    results: z.array(CalibrationResult),
    timing_ms: z.record(z.number()),
    caveat: z.string(),
});
export const SignalDefinition = z.object({
    id: z.string(),
    label: z.string(),
    family: z.string(),
    direction: z.number(),
    description: z.string(),
    rationale: z.string(),
    tags: z.array(z.string()),
    min_bars: z.number(),
});
export const FiredSignal = z.object({
    signal_id: z.string(),
    label: z.string(),
    family: z.string().nullable(),
    direction: z.number(),
    symbol: z.string(),
    ts: z.string(),
    bars_ago: z.number(),
    price: z.number(),
    description: z.string(),
    track_record: z.record(VerdictDetail),
});
// ─────────────────────────── backtest ───────────────────────────
export const Trade = z.object({
    id: z.string(),
    direction: z.string(),
    qty: z.number(),
    entry_bar: z.number(),
    entry_ts: z.string().nullable(),
    entry_price: z.number(),
    exit_bar: z.number().nullable(),
    exit_ts: z.string().nullable(),
    exit_price: z.number().nullable(),
    exit_reason: z.string().nullable(),
    pnl: z.number(),
    pnl_pct: z.number(),
    commission: z.number(),
    mfe: z.number(),
    mae: z.number(),
    bars_held: z.number(),
});
export const BacktestResult = z.object({
    symbol: z.string(),
    trades: z.array(Trade),
    equity: z.array(z.number().nullable()),
    timestamps: z.array(z.string()),
    metrics: z.record(z.unknown()),
    config: z.record(z.unknown()),
    warnings: z.array(z.string()),
    script_warnings: z.array(z.string()).default([]),
    declaration: z.record(z.unknown()),
    assumptions: z.array(z.string()),
    provider: z.string(),
    delayed: z.boolean(),
});
// ─────────────────────────── news ───────────────────────────
export const NewsRow = z.object({
    id: z.number(),
    headline: z.string(),
    summary: z.string().nullable(),
    url: z.string().nullable(),
    source: z.string(),
    published_at: z.string(),
    kind: z.string(),
    sentiment: z.number().nullable(),
    materiality: z.number().nullable(),
    novelty: z.number().nullable(),
    horizon: z.string().nullable(),
    event_type: z.string().nullable(),
    rationale: z.string().nullable(),
    model: z.string().nullable(),
    symbols: z.array(z.string()).nullable(),
});
export const NewsFeed = z.object({
    news: z.array(NewsRow),
    count: z.number(),
    scoring_available: z.boolean(),
    note: z.string().nullable(),
});
/** What an ingest run did. Every count is reported, not inferred: `fetched`
 *  minus `written` is not `collapsed`, because a row can also be dropped for
 *  being outside the window or already stored. */
export const IngestResponse = z.object({
    fetched: z.number(),
    after_dedupe: z.number(),
    collapsed: z.number(),
    written: z.number(),
    sources: z.array(z.string()).default([]),
    errors: z.array(z.string()).default([]),
    providers_configured: z.array(z.string()).default([]),
});
export const ScoreResponse = z.object({
    scored: z.number(),
    attempted: z.number().optional(),
    pending: z.number().optional(),
    usage: z
        .object({
        articles: z.number(),
        input_tokens: z.number().optional(),
        output_tokens: z.number().optional(),
        cache_read_tokens: z.number().optional(),
        cache_hit_ratio: z.number().optional(),
        model: z.string().optional(),
    })
        .optional(),
});
/** Company identity, as far as any configured provider knows it. Every field
 *  past the symbol is optional: Alpaca serves name and exchange but no sector
 *  or market cap, so the header has to render without them. */
export const SymbolDetail = z.object({
    symbol: z.string(),
    name: z.string().nullable().optional(),
    exchange: z.string().nullable().optional(),
    sector: z.string().nullable().optional(),
    market_cap: z.number().nullable().optional(),
    shares_out: z.number().nullable().optional(),
});
export const SentimentSummary = z.object({
    symbol: z.string(),
    articles: z.number(),
    sentiment: z.number().nullable(),
    unweighted_sentiment: z.number().optional(),
    max_materiality: z.number().optional(),
    window_days: z.number().optional(),
    half_life_days: z.number().optional(),
    top_stories: z.array(z.object({
        headline: z.string(),
        url: z.string().nullable(),
        source: z.string(),
        published_at: z.string(),
        sentiment: z.number(),
        materiality: z.number(),
        event_type: z.string().nullable(),
        rationale: z.string().nullable(),
    })).default([]),
    method: z.string().optional(),
});
// ─────────────────────────── research ───────────────────────────
export const Technicals = z.object({
    price: z.number().nullable(),
    rsi14: z.number().nullable(),
    sma20: z.number().nullable(),
    sma50: z.number().nullable(),
    sma200: z.number().nullable(),
    atr14: z.number().nullable(),
    atr_pct: z.number().nullable(),
    bb_position_pct: z.number().nullable(),
    macd_hist: z.number().nullable(),
    adx14: z.number().nullable(),
    high_52w: z.number().nullable(),
    low_52w: z.number().nullable(),
    return_1m_pct: z.number().nullable(),
    return_3m_pct: z.number().nullable(),
    return_12m_pct: z.number().nullable(),
    realised_vol_pct: z.number().nullable(),
    relative_volume: z.number().nullable(),
    pct_off_52w_high: z.number().nullable(),
    pct_above_52w_low: z.number().nullable(),
    above_200dma: z.boolean().nullable(),
});
export const Research = z.object({
    symbol: z.string(),
    timeframe: z.string(),
    quote: z.object({
        price: z.number(),
        change: z.number().nullable(),
        change_pct: z.number().nullable(),
        ts: z.string(),
        delayed: z.boolean(),
    }).nullable(),
    provider: z.string(),
    delayed: z.boolean(),
    bars: z.number(),
    window: z.object({ start: z.string(), end: z.string() }),
    technicals: Technicals,
    signals_fired: z.array(FiredSignal),
    signals_with_measured_edge: z.number(),
    news: z.union([SentimentSummary, z.object({ error: z.string() })]).nullable(),
    interpretation: z.object({
        observations: z.array(z.string()),
        signals_firing: z.number(),
        signals_with_measured_edge: z.number(),
        edge_summary: z.string(),
        /** Shown in the UI, not buried in docs. */
        not_claimed: z.array(z.string()),
    }),
});
export const Health = z.object({
    ok: z.boolean(),
    infra: z.record(z.unknown()),
    providers: z.record(z.unknown()),
    capabilities: z.record(z.boolean()),
    data_quality: z.string(),
});
// ─────────────────────────── presentation helpers ───────────────────────────
/**
 * How a verdict should read in the UI. Centralised so no panel can invent a
 * friendlier label for a result that has not been measured.
 */
export const VERDICT_COPY = {
    "measured-edge": {
        label: "Measured edge",
        tone: "good",
        blurb: "Beat the baseline out-of-sample, on a sample large enough to test, and survived correction for the number of signals tried.",
    },
    "not-significant": {
        label: "Not significant",
        tone: "warn",
        blurb: "Positive on average, but not distinguishable from chance once the number of signals tested is accounted for.",
    },
    "no-edge": {
        label: "No edge",
        tone: "bad",
        blurb: "Did no better than the unconditional baseline over the same bars.",
    },
    untested: {
        label: "Untested",
        tone: "muted",
        blurb: "Too few independent observations to say anything. Not a negative result — an absent one.",
    },
};
export function formatPct(v, digits = 1) {
    if (v === null || v === undefined || !Number.isFinite(v))
        return "—";
    return `${(v * 100).toFixed(digits)}%`;
}
export function formatSigned(v, digits = 2) {
    if (v === null || v === undefined || !Number.isFinite(v))
        return "—";
    return `${v >= 0 ? "+" : ""}${v.toFixed(digits)}`;
}
export function formatNum(v, digits = 2) {
    if (v === null || v === undefined || !Number.isFinite(v))
        return "—";
    return v.toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
}
/**
 * A price, with the currency it is quoted in.
 *
 * Every instrument this platform covers is a US listing, so the symbol is
 * fixed rather than derived. It exists so a number that is money never reads
 * like a number that is not -- a bare 338.41 next to a bare 47.5 (RSI) gives
 * the reader nothing to tell them apart.
 */
export function formatPrice(v, digits = 2) {
    const n = formatNum(v, digits);
    return n === "—" ? n : `$${n}`;
}
/** Seconds per bar, by timeframe id. */
export const TF_SECONDS = {
    "1s": 1, "5s": 5, "10s": 10, "15s": 15, "30s": 30,
    "1m": 60, "2m": 120, "3m": 180, "5m": 300, "10m": 600, "15m": 900,
    "30m": 1800, "45m": 2700, "1h": 3600, "2h": 7200, "3h": 10800, "4h": 14400,
    "1d": 86400, "1w": 604800, "1M": 2592000,
};
/** Seconds in one bar of `timeframe`, or 0 when the id is unknown. */
export function tfSeconds(timeframe) {
    return TF_SECONDS[timeframe] ?? 0;
}
/** A US regular session is 6.5 hours, and a year holds about 252 of them. */
const SESSION_SECONDS = 6.5 * 3600;
const SESSIONS_PER_YEAR = 252;
/**
 * How much calendar time a given number of bars covers, in words.
 *
 * "300 bars" is not an answer to "how far back does this go": on a 5m chart
 * it is two days, on a daily chart fourteen months. The span is what the
 * reader actually wants, so the control shows it alongside the count.
 *
 * Intraday bars only exist during the session, so their span is measured in
 * sessions rather than wall-clock -- 78 five-minute bars is one trading day,
 * not 6.5 hours of a 24-hour day.
 */
export function barSpanLabel(bars, timeframe) {
    const secs = TF_SECONDS[timeframe];
    if (!secs || bars <= 0)
        return "";
    if (secs < 86400) {
        const sessions = (bars * secs) / SESSION_SECONDS;
        if (sessions < 1) {
            const mins = (bars * secs) / 60;
            // Second-level timeframes land here: 300 one-second bars is five
            // minutes, and rounding that to hours printed "0h".
            return mins < 60 ? `${Math.round(mins)}m` : `${Math.round(mins / 60)}h`;
        }
        if (sessions < 21)
            return `${Math.round(sessions)}d`;
        if (sessions < SESSIONS_PER_YEAR)
            return `${Math.round(sessions / 21)}mo`;
        return `${(sessions / SESSIONS_PER_YEAR).toFixed(1)}y`;
    }
    const days = secs === 86400 ? bars : (bars * secs) / 86400;
    const years = secs === 86400 ? bars / SESSIONS_PER_YEAR : days / 365.25;
    if (years < 0.25)
        return `${Math.round(days)}d`;
    if (years < 1)
        return `${Math.round(years * 12)}mo`;
    return `${years.toFixed(years < 10 ? 1 : 0)}y`;
}
/** Compact volume: 1.2M, 843K. */
export function formatVolume(v) {
    if (v === null || v === undefined || !Number.isFinite(v))
        return "—";
    const abs = Math.abs(v);
    if (abs >= 1e9)
        return `${(v / 1e9).toFixed(2)}B`;
    if (abs >= 1e6)
        return `${(v / 1e6).toFixed(2)}M`;
    if (abs >= 1e3)
        return `${(v / 1e3).toFixed(1)}K`;
    return v.toFixed(0);
}
// ─────────────────────────── options ───────────────────────────
export const OptionContract = z.object({
    symbol: z.string(),
    underlying: z.string(),
    type: z.enum(["call", "put"]),
    strike: z.number(),
    expiry: z.string(),
    days_to_expiry: z.number(),
    bid: z.number().nullable(),
    ask: z.number().nullable(),
    last: z.number().nullable(),
    mid: z.number().nullable(),
    volume: z.number(),
    /** null when the provider did not report it — not the same as zero. */
    open_interest: z.number().nullable(),
    iv_provider: z.number().nullable(),
    iv: z.number().nullable(),
    in_the_money: z.boolean(),
    moneyness: z.number(),
    delta: z.number().nullable(),
    gamma: z.number().nullable(),
    theta: z.number().nullable(),
    vega: z.number().nullable(),
    rho: z.number().nullable(),
    prob_itm: z.number().nullable(),
    prob_touch: z.number().nullable(),
    theoretical: z.number().nullable(),
    breakeven: z.number().nullable(),
    spread_pct: z.number().nullable(),
    volume_oi_ratio: z.number().nullable(),
    /** good | fair | poor | untradeable — a zero bid is untradeable at any price. */
    liquidity: z.string(),
    notes: z.array(z.string()),
});
export const ChainSummary = z.object({
    underlying: z.string(),
    expiry: z.string(),
    days_to_expiry: z.number(),
    spot: z.number(),
    atm_iv: z.number().nullable(),
    expected_move_1sd: z.number().nullable(),
    expected_move_pct: z.number().nullable(),
    expected_range: z.array(z.number()).nullable(),
    straddle_price: z.number().nullable(),
    straddle_implied_move_pct: z.number().nullable(),
    put_call_volume_ratio: z.number().nullable(),
    put_call_oi_ratio: z.number().nullable(),
    total_call_volume: z.number(),
    total_put_volume: z.number(),
    total_call_oi: z.number(),
    total_put_oi: z.number(),
    max_pain: z.number().nullable(),
    skew_25d: z.number().nullable(),
    call_oi_walls: z.array(z.object({
        strike: z.number(), open_interest: z.number(), volume: z.number(),
    })),
    put_oi_walls: z.array(z.object({
        strike: z.number(), open_interest: z.number(), volume: z.number(),
    })),
    unusual: z.array(z.object({
        symbol: z.string(), type: z.string(), strike: z.number(), expiry: z.string(),
        volume: z.number(), open_interest: z.number(), volume_oi_ratio: z.number(),
        mid: z.number().nullable(), iv: z.number().nullable(),
        delta: z.number().nullable(), notional: z.number(),
    })),
    notes: z.array(z.string()),
});
export const OptionChain = z.object({
    underlying: z.string(),
    spot: z.number(),
    expiry: z.string(),
    days_to_expiry: z.number(),
    calls: z.array(OptionContract),
    puts: z.array(OptionContract),
    provider: z.string(),
    rate: z.number(),
    dividend_yield: z.number(),
    as_of: z.string(),
    delayed: z.boolean(),
    warnings: z.array(z.string()),
    truncated: z.boolean().optional(),
    summary: ChainSummary,
});
export const Expiry = z.object({
    expiry: z.string(),
    days_to_expiry: z.number(),
    weekly: z.boolean(),
    monthly: z.boolean(),
});
export const StructureLeg = z.object({
    kind: z.enum(["call", "put"]),
    action: z.enum(["buy", "sell"]),
    strike: z.number(),
    price: z.number(),
    quantity: z.number(),
    delta: z.number().nullable(),
    symbol: z.string().nullable(),
});
export const SignalComparison = z.object({
    comparable: z.boolean(),
    reason: z.string().optional(),
    implied_prob_profit: z.number().optional(),
    measured_hit_rate: z.number().optional(),
    measured_interval: z.array(z.number()).optional(),
    measured_samples: z.number().optional(),
    baseline_hit_rate: z.number().nullable().optional(),
    raw_difference: z.number().optional(),
    horizon_bars: z.number().optional(),
    /** edge-vs-market is the only value that indicates the signal beat the price. */
    verdict: z.enum(["edge-vs-market", "no-edge-vs-market", "insufficient-sample"]).optional(),
    explanation: z.string().optional(),
    caveats: z.array(z.string()).optional(),
});
export const Structure = z.object({
    id: z.string(),
    name: z.string(),
    view: z.string(),
    legs: z.array(StructureLeg),
    net_debit: z.number().nullable(),
    max_profit: z.number().nullable(),
    max_loss: z.number().nullable(),
    breakevens: z.array(z.number()),
    prob_profit: z.number().nullable(),
    expected_value: z.number().nullable(),
    risk_reward: z.number().nullable(),
    capital_required: z.number().nullable(),
    net_delta: z.number().nullable(),
    net_theta: z.number().nullable(),
    net_vega: z.number().nullable(),
    liquidity: z.string(),
    warnings: z.array(z.string()),
    rationale: z.string(),
    vs_signal: SignalComparison.optional(),
});
export const OptionSuggestion = z.object({
    symbol: z.string(),
    spot: z.number(),
    expiry: z.string(),
    days_to_expiry: z.number(),
    inferred_view: z.string(),
    atm_iv: z.number().nullable(),
    expected_move: z.number().nullable(),
    expected_move_pct: z.number().nullable(),
    expected_range: z.array(z.number()).nullable(),
    signals: z.array(z.object({
        signal_id: z.string(),
        label: z.string(),
        direction: z.number(),
        bars_ago: z.number(),
        verdict: z.string().nullable(),
        hit_rate: z.number().nullable(),
        baseline: z.number().nullable(),
        samples: z.number().nullable(),
        weight: z.number(),
    })),
    signals_with_measured_edge: z.number(),
    compared_against: z.record(z.unknown()).nullable(),
    structures: z.array(Structure),
    headline: z.string(),
    not_claimed: z.array(z.string()),
});
export const VrpResult = z.object({
    symbol: z.string(),
    source: z.string(),
    horizon_days: z.number(),
    observations: z.number(),
    overlapping_observations: z.number(),
    implied_exceeded_realised: z.number(),
    hit_rate: z.number(),
    ci_low: z.number(),
    ci_high: z.number(),
    mean_premium: z.number(),
    median_premium: z.number(),
    mean_implied: z.number(),
    mean_realised: z.number(),
    worst_premium: z.number(),
    window: z.object({ start: z.string(), end: z.string() }),
    notes: z.array(z.string()),
});
export const VrpUnavailable = z.object({
    error: z.string(),
    symbol: z.string().optional(),
    snapshots: z.number().optional(),
    needed: z.number().optional(),
    hint: z.string().optional(),
});
export const VrpResponse = z.union([VrpResult, VrpUnavailable]);
/** Colour coding for a contract's tradeability. */
export const LIQUIDITY_TONE = {
    good: "good",
    fair: "muted",
    poor: "warn",
    untradeable: "bad",
    unknown: "muted",
};
// ─────────────────────────── indicator catalogue ───────────────────────────
export const IndicatorParam = z.object({
    name: z.string(),
    kind: z.enum(["int", "float", "bool", "source", "string"]),
    default: z.unknown(),
    label: z.string(),
    minval: z.number().nullable(),
    maxval: z.number().nullable(),
    step: z.number().nullable(),
    options: z.array(z.string()).nullable(),
});
export const IndicatorPlotStyle = z.object({
    name: z.string(),
    color: z.string(),
    kind: z.string(),
    linewidth: z.number(),
    opacity: z.number(),
});
export const IndicatorLevel = z.object({
    value: z.number(),
    label: z.string(),
    color: z.string(),
    style: z.string(),
});
export const IndicatorFill = z.object({
    plot_a: z.string(),
    plot_b: z.string(),
    color: z.string(),
});
export const IndicatorDef = z.object({
    id: z.string(),
    name: z.string(),
    short: z.string(),
    category: z.string(),
    /** overlay draws on the price pane; separate gets its own pane. */
    pane: z.enum(["overlay", "separate"]),
    params: z.array(IndicatorParam),
    plots: z.array(IndicatorPlotStyle),
    levels: z.array(IndicatorLevel),
    fills: z.array(IndicatorFill),
    description: z.string(),
    min_bars: z.number(),
    scale: z.array(z.number()).nullable(),
    notes: z.array(z.string()),
});
export const IndicatorCatalogue = z.object({
    indicators: z.array(IndicatorDef),
    categories: z.array(z.string()),
    count: z.number(),
    note: z.string(),
});
export const IndicatorSeries = z.object({
    name: z.string(),
    values: z.array(z.number().nullable()),
    color: z.string(),
    kind: z.string(),
    linewidth: z.number(),
    opacity: z.number(),
});
export const IndicatorResult = z.object({
    instance_id: z.string(),
    indicator_id: z.string(),
    name: z.string(),
    short: z.string(),
    pane: z.enum(["overlay", "separate"]),
    scale: z.array(z.number()).nullable(),
    levels: z.array(IndicatorLevel),
    fills: z.array(IndicatorFill),
    notes: z.array(z.string()),
    params: z.record(z.unknown()),
    plots: z.array(IndicatorSeries),
});
export const IndicatorRun = z.object({
    symbol: z.string(),
    timeframe: z.string(),
    times: z.array(z.number()),
    bars: z.number(),
    warmup_bars_used: z.number(),
    provider: z.string(),
    delayed: z.boolean(),
    instances: z.array(IndicatorResult),
    elapsed_ms: z.number(),
    warnings: z.array(z.string()),
});
// ─────────────────────────── chart events ───────────────────────────
export const MarketEvent = z.object({
    kind: z.enum(["earnings", "dividend", "split", "news"]),
    ts: z.string(),
    /** Single-character glyph for the marker. */
    label: z.string(),
    title: z.string(),
    detail: z.string(),
    tone: z.enum(["good", "bad", "neutral"]),
    upcoming: z.boolean().default(false),
    data: z.record(z.unknown()).default({}),
});
export const NextEarnings = z.object({
    date: z.string(),
    days_away: z.number(),
    eps_average: z.number().nullable(),
    eps_low: z.number().nullable(),
    eps_high: z.number().nullable(),
    revenue_average: z.number().nullable(),
    ex_dividend_date: z.string().nullable(),
    note: z.string(),
});
export const EventsResponse = z.object({
    symbol: z.string(),
    events: z.array(MarketEvent),
    counts: z.record(z.number()),
    next_earnings: NextEarnings.nullable(),
    note: z.string(),
});
/**
 * Marker colours: saturated neon, legible as a filled 16px disc.
 *
 * Neon works here because the strip sits on the darkest surface in the app
 * and holds nothing else -- the same colours on a panel would shout over the
 * data.
 *
 * Hue separation matters more than it looks: at 16px, across the width of a
 * chart, hue is the only thing telling two discs apart. An early version put
 * earnings on amber at 41 degrees against news at 67 -- 26 apart -- and the
 * two read as the same colour.
 *
 * Splits are pink by explicit choice. Earnings moved to make room: it was
 * rose at 349 degrees, 19 from pink, which would have been the worst pair in
 * any version of this palette. With pink, cyan and yellow fixed, red-orange
 * at 18 degrees is the best remaining slot for earnings, and no pair is
 * closer than 48 degrees.
 *
 * Kinds are distinguished by hue *and* by the letter inside, never by
 * brightness alone, so the strip stays readable to a colour-blind reader.
 * Every one of these is light enough to need a dark letter -- see onColor,
 * which derives that rather than assuming it.
 */
export const EVENT_COLOR = {
    earnings: "#ff4d00", // neon red-orange  18°
    dividend: "#00d9ff", // neon cyan       189°
    split: "#ff2d95", // neon pink       330°
    news: "#e5ff1f", // neon yellow      67°
};
// True black, not the app's near-black surface colour. At 9px bold over a
// neon fill the two are not interchangeable: #0a0d13 carries a blue cast that
// reads as dark grey once the glyph is antialiased down to a few pixels wide.
const INK = "#000000";
const PAPER = "#ffffff";
/** WCAG sRGB relative luminance. */
function luminance(hex) {
    const h = hex.replace("#", "");
    const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    if (full.length !== 6)
        return null;
    const n = Number.parseInt(full, 16);
    if (!Number.isFinite(n))
        return null;
    const lin = (v) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
}
/**
 * Black or white for a letter sitting on `hex`, whichever is actually more
 * legible.
 *
 * The obvious implementation thresholds the fill's luminance, and it is
 * wrong: a luminance cutoff put white on neon magenta at 2.6:1 -- worse than
 * no styling -- because magenta is dark enough to fall on the light-text side
 * while still being far too saturated for white to read on. Contrast is not
 * monotonic in luminance around the middle of the range, so the two candidates
 * are compared directly instead of inferred from one number.
 */
export function onColor(hex) {
    const l = luminance(hex);
    if (l === null)
        return PAPER;
    const against = (other) => {
        const o = luminance(other) ?? 0;
        const [hi, lo] = l > o ? [l, o] : [o, l];
        return (hi + 0.05) / (lo + 0.05);
    };
    return against(INK) >= against(PAPER) ? INK : PAPER;
}
// ─────────────────────────── data sources ───────────────────────────
export const DataSource = z.object({
    id: z.string(),
    name: z.string(),
    env_vars: z.array(z.string()),
    signup_url: z.string(),
    keys_url: z.string(),
    unlocks: z.array(z.string()),
    cost: z.string(),
    required: z.boolean(),
    free_tier: z.string().nullable(),
    configured: z.boolean(),
});
export const Limitation = z.object({
    /** What the user actually sees. */
    symptom: z.string(),
    because: z.string(),
    fix: z.string(),
});
export const Alternative = z.object({
    name: z.string(),
    url: z.string(),
    covers: z.string(),
    cost: z.string(),
    verdict: z.string(),
    /** True when this codebase already has an adapter for it. */
    supported: z.boolean(),
});
export const SourcesStatus = z.object({
    sources: z.array(DataSource),
    alternatives: z.array(Alternative).default([]),
    configured_count: z.number(),
    total: z.number(),
    limitations: z.array(Limitation),
    env_file: z.string(),
    headline: z.string(),
});
